export type OnboardingDecisionStatus =
  | 'completed_by_statura'
  | 'manual_setup_chosen'
  | 'deferred'
  | 'not_applicable'
  | 'blocked_by_release';

export type OnboardingDecisionOwner = 'provider' | 'statura' | 'joint';

export type OnboardingGoLiveImpact = 'can_proceed' | 'conditional' | 'blocked';

export interface OnboardingStepDecisionInput {
  status: OnboardingDecisionStatus;
  owner: OnboardingDecisionOwner;
  reason: string;
  goLiveImpact: OnboardingGoLiveImpact;
  targetDate?: string | null;
}

export interface OnboardingStepDecision extends OnboardingStepDecisionInput {
  decidedAt: string;
  source: 'setup_wizard';
}

export interface OnboardingProgress {
  /** Current step ID (string, not number — stable across plan changes) */
  currentStep: string;
  /** Step IDs visible under the tenant's current plan/module/go-live scope */
  visibleStepIds?: string[];
  /** Step IDs that have been completed in the wizard */
  completedSteps: string[];
  /** Step IDs that have an explicit non-complete readiness decision */
  skippedSteps: string[];
  /** Per-step readiness decisions for manual setup, deferral, Statura completion, N/A, or release blocks */
  stepDecisions?: Record<string, OnboardingStepDecision>;
  /** Per-step data cache (for pre-filling on revisit) */
  stepData: Record<string, Record<string, unknown>>;
  /** When the wizard was first started */
  startedAt: string;
  /** Last progress update timestamp */
  lastUpdatedAt: string;
}

export interface OnboardingProgressSummary {
  completedSteps: number;
  decisionSteps: number;
  resolvedSteps: number;
  deferredSteps: number;
  blockedSteps: number;
  totalSteps?: number;
}

export interface TenantStateOnboardingReconciliationInput {
  progress: OnboardingProgress | null;
  visibleStepIds: readonly string[];
  detectedCompletedStepIds: readonly string[];
  detectedStepData?: Record<string, Record<string, unknown>>;
  now?: string;
}

const DEFAULT_LEGACY_SKIP_DECISION: OnboardingStepDecisionInput = {
  status: 'deferred',
  owner: 'provider',
  reason: 'Marked to do later before setup-decision capture was introduced.',
  goLiveImpact: 'conditional',
  targetDate: null,
};

export function createInitialProgress(firstStepId: string): OnboardingProgress {
  return {
    currentStep: firstStepId,
    completedSteps: [],
    skippedSteps: [],
    stepDecisions: {},
    stepData: {},
    startedAt: new Date().toISOString(),
    lastUpdatedAt: new Date().toISOString(),
  };
}

function uniqueStrings(values: readonly string[] | undefined): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values ?? []) {
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

function hasStepData(value: Record<string, unknown> | undefined): boolean {
  return Boolean(value && Object.keys(value).length > 0);
}

export function normaliseStepDecision(
  decision: OnboardingStepDecisionInput,
  decidedAt = new Date().toISOString(),
): OnboardingStepDecision {
  return {
    ...decision,
    reason: decision.reason.trim(),
    targetDate: decision.targetDate || null,
    decidedAt,
    source: 'setup_wizard',
  };
}

export function isGoLiveBlockingDecision(decision: OnboardingStepDecision | undefined): boolean {
  return decision?.status === 'blocked_by_release' || decision?.goLiveImpact === 'blocked';
}

export function isSetupResolvedByDecision(decision: OnboardingStepDecision | undefined): boolean {
  if (!decision) return false;
  return !isGoLiveBlockingDecision(decision);
}

/**
 * Advance progress to the next step after completing a step or recording a
 * manual/deferred readiness decision. Legacy `skip` calls are preserved for
 * older call sites but new callers should pass an explicit decision object.
 */
export function advanceProgress(
  progress: OnboardingProgress,
  completedStepId: string,
  nextStepId: string,
  action: 'complete' | 'skip' | OnboardingStepDecisionInput,
  stepData?: Record<string, unknown>,
): OnboardingProgress {
  const updated: OnboardingProgress = {
    ...progress,
    completedSteps: [...progress.completedSteps],
    skippedSteps: [...progress.skippedSteps],
    stepDecisions: { ...(progress.stepDecisions ?? {}) },
    stepData: { ...progress.stepData },
  };

  if (action === 'complete') {
    if (!updated.completedSteps.includes(completedStepId)) {
      updated.completedSteps = [...updated.completedSteps, completedStepId];
    }
    updated.skippedSteps = updated.skippedSteps.filter((id) => id !== completedStepId);
    delete updated.stepDecisions?.[completedStepId];
  } else {
    const decision =
      action === 'skip'
        ? normaliseStepDecision(DEFAULT_LEGACY_SKIP_DECISION)
        : normaliseStepDecision(action);

    if (!updated.skippedSteps.includes(completedStepId)) {
      updated.skippedSteps = [...updated.skippedSteps, completedStepId];
    }
    updated.completedSteps = updated.completedSteps.filter((id) => id !== completedStepId);
    updated.stepDecisions = {
      ...(updated.stepDecisions ?? {}),
      [completedStepId]: decision,
    };
  }

  if (stepData) {
    updated.stepData = { ...updated.stepData, [completedStepId]: stepData };
  }

  updated.currentStep = nextStepId;
  updated.lastUpdatedAt = new Date().toISOString();

  return updated;
}

export function reconcileOnboardingProgressWithTenantState({
  progress,
  visibleStepIds,
  detectedCompletedStepIds,
  detectedStepData,
  now = new Date().toISOString(),
}: TenantStateOnboardingReconciliationInput): OnboardingProgress {
  const visibleSteps = uniqueStrings(visibleStepIds);
  const firstStepId = visibleSteps[0] ?? progress?.currentStep ?? 'welcome';
  const visibleStepSet = new Set(visibleSteps);
  const detectedStepSet = new Set(
    uniqueStrings(detectedCompletedStepIds).filter((stepId) => visibleStepSet.has(stepId)),
  );

  const base = progress ?? createInitialProgress(firstStepId);
  const stepDecisions = { ...(base.stepDecisions ?? {}) };
  const completedSteps = uniqueStrings(base.completedSteps);
  const skippedSteps = uniqueStrings(base.skippedSteps);
  const stepData = { ...(base.stepData ?? {}) };

  for (const stepId of visibleSteps) {
    if (!detectedStepSet.has(stepId)) continue;
    if (isGoLiveBlockingDecision(stepDecisions[stepId])) continue;

    if (!completedSteps.includes(stepId)) {
      completedSteps.push(stepId);
    }
    const skippedIndex = skippedSteps.indexOf(stepId);
    if (skippedIndex >= 0) skippedSteps.splice(skippedIndex, 1);
    delete stepDecisions[stepId];

    const detectedData = detectedStepData?.[stepId];
    if (detectedData && hasStepData(detectedData) && !hasStepData(stepData[stepId])) {
      stepData[stepId] = detectedData;
    }
  }

  const completedStepSet = new Set(completedSteps);
  const skippedStepSet = new Set(skippedSteps);
  const isResolved = (stepId: string): boolean => {
    if (completedStepSet.has(stepId)) return true;
    const decision = stepDecisions[stepId];
    if (isSetupResolvedByDecision(decision)) return true;
    return skippedStepSet.has(stepId) && !decision;
  };

  let currentStep = base.currentStep;
  const currentIndex = visibleSteps.indexOf(currentStep);
  if (!visibleStepSet.has(currentStep) || isResolved(currentStep)) {
    const searchStart = currentIndex >= 0 ? currentIndex + 1 : 0;
    currentStep =
      visibleSteps.slice(searchStart).find((stepId) => !isResolved(stepId)) ??
      visibleSteps.find((stepId) => !isResolved(stepId)) ??
      visibleSteps[visibleSteps.length - 1] ??
      firstStepId;
  }

  const nextProgress: OnboardingProgress = {
    ...base,
    currentStep,
    visibleStepIds: visibleSteps,
    completedSteps,
    skippedSteps,
    stepDecisions,
    stepData,
  };

  const before = {
    currentStep: base.currentStep,
    visibleStepIds: base.visibleStepIds ?? [],
    completedSteps: base.completedSteps,
    skippedSteps: base.skippedSteps,
    stepDecisions: base.stepDecisions ?? {},
    stepData: base.stepData ?? {},
  };
  const after = {
    currentStep: nextProgress.currentStep,
    visibleStepIds: nextProgress.visibleStepIds ?? [],
    completedSteps: nextProgress.completedSteps,
    skippedSteps: nextProgress.skippedSteps,
    stepDecisions: nextProgress.stepDecisions ?? {},
    stepData: nextProgress.stepData ?? {},
  };

  return {
    ...nextProgress,
    lastUpdatedAt: JSON.stringify(before) === JSON.stringify(after) ? base.lastUpdatedAt : now,
  };
}

export function hasGoLiveBlockingOnboardingDecision(
  progress: OnboardingProgress,
  visibleStepIds?: readonly string[],
): boolean {
  const visibleStepSet = visibleStepIds ? new Set(visibleStepIds) : null;
  return Object.entries(progress.stepDecisions ?? {}).some(
    ([stepId, decision]) =>
      (!visibleStepSet || visibleStepSet.has(stepId)) && isGoLiveBlockingDecision(decision),
  );
}

export function getOnboardingProgressSummary(
  progress: Pick<OnboardingProgress, 'completedSteps' | 'skippedSteps' | 'stepDecisions'> | null,
  totalSteps?: number,
  visibleStepIds?: readonly string[],
): OnboardingProgressSummary {
  const visibleStepSet = visibleStepIds ? new Set(visibleStepIds) : null;
  const isVisibleStep = (stepId: string) => !visibleStepSet || visibleStepSet.has(stepId);

  if (!progress) {
    return {
      completedSteps: 0,
      decisionSteps: 0,
      resolvedSteps: 0,
      deferredSteps: 0,
      blockedSteps: 0,
      totalSteps,
    };
  }

  const completed = new Set((progress.completedSteps ?? []).filter(isVisibleStep));
  const decisions = progress.stepDecisions ?? {};
  const decisionEntries = Object.entries(decisions).filter(([stepId]) => isVisibleStep(stepId));
  const legacySkippedSteps = (progress.skippedSteps ?? []).filter(
    (stepId) => isVisibleStep(stepId) && !decisions[stepId],
  );
  const resolvedByDecision = decisionEntries
    .filter(([, decision]) => isSetupResolvedByDecision(decision))
    .map(([stepId]) => stepId);
  const resolved = new Set([...completed, ...legacySkippedSteps, ...resolvedByDecision]);
  const deferredSteps = decisionEntries.filter(([, decision]) => decision.status === 'deferred');
  const blockedSteps = decisionEntries.filter(([, decision]) => isGoLiveBlockingDecision(decision));

  return {
    completedSteps: completed.size,
    decisionSteps: decisionEntries.length,
    resolvedSteps: resolved.size,
    deferredSteps: deferredSteps.length + legacySkippedSteps.length,
    blockedSteps: blockedSteps.length,
    totalSteps,
  };
}

export function formatOnboardingDecisionStatus(status: OnboardingDecisionStatus): string {
  switch (status) {
    case 'completed_by_statura':
      return 'Completed by Statura';
    case 'manual_setup_chosen':
      return 'Manual setup chosen';
    case 'deferred':
      return 'Deferred';
    case 'not_applicable':
      return 'Not applicable';
    case 'blocked_by_release':
      return 'Blocked by release';
  }
}
